/**
 * What our own printable signs SAY, in English and Spanish as a matched pair (the owner: "two languages", like the English and
 * Spanish county sheets posted side by side). The PDF drawing is in @logwave/pdf (signs.ts); the words live here so the tests
 * read the same text the printer draws.
 *
 * Three rules these signs keep:
 *  - P1 Content only. We make a printable only where the law asks for the content (a handwashing reminder, a consumer advisory,
 *    a written allergen notice), never a copy of a government sign. A government sign is linked, not copied (signs.ts).
 *  - P2 Never the government's. No county or state name stands in the title, no government logo is drawn, and the allergen list says
 *    in its footer that it is the business's own list and not the Health Department's sign (test: signprint.test.ts).
 *  - P3 Two languages. Every printable has English and Spanish text with the same number of lines. The Spanish is written by the build
 *    team in plain Latin American Spanish and is flagged SIGN_ES_STATUS = "needs native review" until a native speaker with food-service
 *    experience signs off (docs/I18N.md; the same rule as checkinEs.ts). It never claims to be reviewed.
 *
 * Sources for the English wording:
 *  - Handwashing: Food Code 2022 6-301.14 "A sign or poster that notifies FOOD EMPLOYEES to wash their hands shall be provided at all
 *    HANDWASHING SINKS used by FOOD EMPLOYEES and shall be clearly visible to FOOD EMPLOYEES." and 2-301.12(A), (B): at least 20 seconds,
 *    rinse under warm running water, cleaning compound, rub 10 to 15 seconds with attention under the fingernails, rinse, dry (6-301.12).
 *  - Consumer advisory: 3-603.11(B)(2) footnote "These items are served raw or undercooked, or contain (or may contain) raw or undercooked
 *    ingredients" and (C) reminder "Consuming raw or undercooked meats, poultry, seafood, shellfish, or eggs may increase your risk of
 *    foodborne illness" (the words are core advisory.ts DISCLOSURE_FOOTNOTE and REMINDERS).
 *  - Allergen list: 3-602.12(C) "written notification of the presence of major food allergens as an ingredient in unpackaged food
 *    items that are served or sold to the consumer"; the nine are 1-201.10(B).
 */
import { DISCLOSURE_FOOTNOTE, REMINDERS } from "./advisory.ts";
import { NINE, shortLabel } from "./allergens.ts";

export const SIGN_ES_STATUS = "needs native review" as const;
export type SignLang = "en" | "es";
export const SIGN_LANGS: readonly SignLang[] = ["en", "es"];
export const LANG_NAME: Record<SignLang, string> = { en: "English", es: "Español" };

const poss = (n: string) => (/s$/i.test(n) ? `${n}'` : `${n}'s`);

/** One language of one printable sign. */
export interface SignText {
  title: string;
  /** One line under the title. */
  lead?: string;
  /** A short list drawn with large type (allergen names). */
  items?: string[];
  /** Numbered steps. */
  steps?: string[];
  /** Plain lines under the body. */
  notes?: string[];
  /** The small print: whose sign this is and what it is not. */
  footer: string;
}
export type PrintableId = "allergen-list" | "handwashing" | "advisory";
export interface PrintableSign { id: PrintableId; en: SignText; es: SignText; esStatus: typeof SIGN_ES_STATUS; citation: string }

/** The nine, in Spanish, in the same order as NINE. */
export const ALLERGEN_ES: Record<string, string> = {
  milk: "Leche", eggs: "Huevos", peanuts: "Cacahuates (maní)", "tree nuts": "Nueces y frutos secos de árbol", soy: "Soya", wheat: "Trigo",
  fish: "Pescado", shellfish: "Mariscos (camarón, cangrejo, langosta)", sesame: "Ajonjolí (sésamo)",
};

/**
 * Our own allergen list. `present` is the allergens the place's food and dishes point to (signs.ts presentAllergens); an empty list
 * is refused (a list that says nothing is not a sign). It is NOT the county's sign and says so in both languages (P2).
 */
export function allergenListSign(present: readonly string[], business: string): PrintableSign {
  const ids = NINE.filter((id) => present.includes(id));
  if (!ids.length) throw new Error("No allergen to list: this place needs no allergen list yet.");
  const who = business.trim() || "This business";
  return {
    id: "allergen-list",
    citation: "Food Code 3-602.12(C); 1-201.10(B)",
    esStatus: SIGN_ES_STATUS,
    en: {
      title: "Allergens in our food",
      lead: "Food here contains, or may contain, these major food allergens:",
      items: ids.map(shortLabel),
      notes: ["Please ask a manager if you have any questions.", "We share kitchen equipment, so cross-contact is possible with any item."],
      footer: `${poss(who)} own list, made from the food we keep. It is not a Health Department sign.`,
    },
    es: {
      title: "Alérgenos en nuestra comida",
      lead: "La comida aquí contiene, o puede contener, estos alérgenos alimentarios principales:",
      items: ids.map((id) => ALLERGEN_ES[id]!),
      notes: ["Por favor pregunte a un gerente si tiene alguna duda.", "Compartimos el equipo de cocina, así que puede haber contacto cruzado con cualquier producto."],
      footer: `Lista propia de ${who}, hecha con la comida que tenemos. No es un letrero del Departamento de Salud.`,
    },
  };
}

/** The handwashing reminder for every handwashing sink (6-301.14). */
export function handwashingSign(business = ""): PrintableSign {
  const who = business.trim();
  return {
    id: "handwashing",
    citation: "Food Code 6-301.14, 2-301.12",
    esStatus: SIGN_ES_STATUS,
    en: {
      title: "Wash your hands",
      lead: "Food workers: wash at this sink, at least 20 seconds.",
      steps: [
        "Rinse hands and arms under warm running water.",
        "Add soap.",
        "Rub hands and arms hard for 10 to 15 seconds. Clean under the fingernails and between the fingers.",
        "Rinse well under warm running water.",
        "Dry with a paper towel.",
      ],
      notes: ["Hand sanitizer does not replace washing."],
      footer: `${who ? `${who}. ` : ""}Food Code 6-301.14 asks for a sign or poster at every handwashing sink.`,
    },
    es: {
      title: "Lávese las manos",
      lead: "Trabajadores de alimentos: láveselas en este lavabo, por lo menos 20 segundos.",
      steps: [
        "Enjuague las manos y los brazos con agua tibia corriente.",
        "Ponga jabón.",
        "Frote las manos y los brazos con fuerza de 10 a 15 segundos. Limpie debajo de las uñas y entre los dedos.",
        "Enjuague bien con agua tibia corriente.",
        "Seque con una toalla de papel.",
      ],
      notes: ["El desinfectante de manos no reemplaza el lavado."],
      footer: `${who ? `${who}. ` : ""}El Código de Alimentos 6-301.14 pide un letrero o cartel en cada lavabo de manos.`,
    },
  };
}

/** The consumer advisory footnote and reminder for the menu or a placard (3-603.11(B)(2), (C)). */
export function advisorySign(business = ""): PrintableSign {
  const who = business.trim();
  return {
    id: "advisory",
    citation: "Food Code 3-603.11",
    esStatus: SIGN_ES_STATUS,
    en: {
      title: "Raw or undercooked food",
      lead: `* ${DISCLOSURE_FOOTNOTE}.`,
      notes: [`${REMINDERS[1]}.`, `${REMINDERS[0]}.`],
      footer: `${who ? `${who}. ` : ""}Put an asterisk (*) next to each item on the menu that is served raw or undercooked.`,
    },
    es: {
      title: "Comida cruda o poco cocida",
      lead: "* Estos productos se sirven crudos o poco cocidos, o contienen (o pueden contener) ingredientes crudos o poco cocidos.",
      notes: ["Comer carne, aves, pescado, mariscos o huevos crudos o poco cocidos puede aumentar su riesgo de contraer una enfermedad transmitida por alimentos.", "En cuanto a la seguridad de estos productos, hay información escrita disponible si la solicita."],
      footer: `${who ? `${who}. ` : ""}Ponga un asterisco (*) junto a cada producto del menú que se sirva crudo o poco cocido.`,
    },
  };
}

/** The printable for an id, with the place's allergens for the allergen list. Undefined = no printable of ours for that id. */
export function printableFor(id: PrintableId, o: { business?: string; allergens?: readonly string[] } = {}): PrintableSign | undefined {
  if (id === "allergen-list") return o.allergens?.length ? allergenListSign(o.allergens, o.business ?? "") : undefined;
  if (id === "handwashing") return handwashingSign(o.business);
  if (id === "advisory") return advisorySign(o.business);
  return undefined;
}

/** Every line of one language, for tests and the PDF's text pass. */
export const textLines = (t: SignText): string[] => [t.title, ...(t.lead ? [t.lead] : []), ...(t.items ?? []), ...(t.steps ?? []), ...(t.notes ?? []), t.footer];
